import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import "../../../firebase";
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Wizard page components
//import About from "layouts/applications/wizard/components/About";
import Account from "layouts/pages/wizard/components/Account";
import Address from "layouts/applications/wizard/components/Address";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "layouts/authentication/components/Footer";
import About from "./components/About";

const db = getFirestore();

function Wizard() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [profession, setProfession] = useState('');
  const [design, setDesign] = useState(false);
  const [code, setCode] = useState(false);
  const [develop, setDevelop] = useState(false);
  const steps = ["About", "Profession"];
  const isLastStep = activeStep === steps.length - 1;
  const [profilePic, setProfilePic] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setFirstName(userData.firstname || '');
          setLastName(userData.lastname || '');
          setEmail(userData.email || '');
          updateProfession(userData.profession || '');
          setProfilePic(userData.profilePic || '');
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const updateProfession = (newProfession) => {
    setProfession(newProfession);
    setDesign(newProfession === "Medical");
    setCode(newProfession === "Therapy");
    setDevelop(newProfession === "Psychiatry");
  };

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const auth = getAuth();
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      const userDocRef = doc(db, 'users', uid);
      const userData = { firstname: firstName, lastname: lastName, email: email, profession: profession, profilePic: profilePic };

      try {
        await setDoc(userDocRef, userData, { merge: true });
        if (isLastStep) {

          const GHLData = { firstname: firstName, lastname: lastName, email: email, profession: profession };
          fetch('https://services.leadconnectorhq.com/hooks/scTZfdVrJb3bMVHNemTZ/webhook-trigger/bb7c2df1-43d2-432d-a5ff-e6969aae6c9b', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(GHLData),
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('CRM update failed');
            }
            return response.json(); // You can process the response if needed
          })
          .catch((error) => {
            console.error('CRM Update Error:', error);
            // error handling, notification in firebase 
          });

          navigate('/pages/profile-overview');
        } else {
          handleNext();
        }
      } catch (e) {
        console.error("Error adding document: ", e);
      }
    }
  };

  const handleProfilePicChange = async (file) => {
    if (!file) return;
    const auth = getAuth();
    if (!auth.currentUser) {
      console.log("User not authenticated");
      return;
    }

    const storage = getStorage();
    const userId = auth.currentUser.uid;
    const storageRef = ref(storage, `user_photos/${userId}/${file.name}`); // Adjust path as needed

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(snapshot.ref);
      setProfilePic(photoURL);
    } catch (error) {
      console.error("Error uploading file: ", error);
    }
  };

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return <About firstName={firstName} setFirstName={setFirstName} lastName={lastName} setLastName={setLastName} email={email} setEmail={setEmail} profilePic={profilePic} onProfilePicChange={handleProfilePicChange} />;
      case 1:
        return <Account setProfession={updateProfession} design={design} code={code} develop={develop} />;
      //case 2:
        //return <Address />;
      default:
        return null;
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pt={3} pb={8}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={10} xl={10} xxl={8}>
            <SoftBox mt={6} mb={1} textAlign="center">
              <SoftBox mb={1}>
                <SoftTypography variant="h3" fontWeight="bold">Build Your Profile</SoftTypography>
              </SoftBox>
              <SoftTypography variant="h5" fontWeight="regular" color="secondary">
              This information will enable us to personalize your account and ensuring it is configured with the appropriate default settings and notes tailored specifically to your profession&apos;s needs.              
              </SoftTypography>
            </SoftBox>

            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Card>
              <SoftBox p={2}>
                {getStepContent(activeStep)}
                <SoftBox mt={3} width="100%" display="flex" justifyContent="space-between">
                  {activeStep === 0 ? <SoftBox /> : <SoftButton variant="gradient" color="light" onClick={handleBack}>back</SoftButton>}
                  <SoftButton variant="gradient" color="dark" onClick={handleSubmit}>
                    {isLastStep ? "continue" : "next"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Wizard;
